import { useEffect, useState } from 'react'
import { readBuildSha, isNewBuild } from '../utils/buildVersion'
import './UpdatePrompt.css'

// Offers a refresh when a newer build has been deployed than the one this tab
// is running. A tab left open across a push otherwise keeps the old bundle
// indefinitely — the old UI, talking to the new API.
//
// Asks rather than reloads: the person may be halfway through a form, and a
// forced reload would throw that away.
//
// Checked every few minutes and whenever the tab becomes visible again, which
// is when a stale tab is most likely to be picked back up. Rendered next to
// VersionBadge in App.jsx, outside <Routes>, so it works on every page.
const CHECK_INTERVAL_MS = 5 * 60 * 1000

async function fetchDeployedSha() {
  try {
    // no-store plus a throwaway query so neither the browser nor an
    // intermediate cache can answer with the version.json we already saw.
    const res = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store' })
    if (!res.ok) return null
    return readBuildSha(await res.json())
  } catch {
    // Offline, or the SPA fallback served index.html (no version.json yet) —
    // either way, nothing to report.
    return null
  }
}

function UpdatePrompt() {
  // The sha of the newer build, once one is seen. Kept rather than a boolean
  // so "Later" can dismiss THAT build and still prompt for the one after it.
  const [deployedSha, setDeployedSha] = useState(null)
  const [dismissedSha, setDismissedSha] = useState(null)

  useEffect(() => {
    let cancelled = false

    const check = async () => {
      const sha = await fetchDeployedSha()
      if (!cancelled && isNewBuild(__BUILD_SHA__, sha)) setDeployedSha(sha)
    }

    const onVisible = () => {
      if (document.visibilityState === 'visible') check()
    }

    const timer = setInterval(check, CHECK_INTERVAL_MS)
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      cancelled = true
      clearInterval(timer)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [])

  if (!deployedSha || deployedSha === dismissedSha) return null

  return (
    <div className="update-prompt" role="status">
      <span className="update-prompt-text">A new version of the site is available.</span>
      <div className="update-prompt-actions">
        <button
          type="button"
          className="update-prompt-btn update-prompt-btn-primary"
          onClick={() => window.location.reload()}
        >
          Refresh
        </button>
        <button
          type="button"
          className="update-prompt-btn"
          onClick={() => setDismissedSha(deployedSha)}
        >
          Later
        </button>
      </div>
    </div>
  )
}

export default UpdatePrompt
